import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import { Box, Button, Divider, Popover, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useListContext, useTranslate } from 'react-admin';
import { Iconify } from 'src/components/iconify';

type QuickRange = '24h' | '7d' | '30d' | 'today' | 'yesterday' | 'this_week' | 'this_month';

dayjs.extend(utc);

const getRange = (value: QuickRange): { from: string; to: string } => {
  const now = dayjs.utc();
  switch (value) {
    case '24h':
      return { from: now.subtract(24, 'hour').toISOString(), to: now.toISOString() };
    case '7d':
      return { from: now.subtract(7, 'day').toISOString(), to: now.toISOString() };
    case '30d':
      return { from: now.subtract(30, 'day').toISOString(), to: now.toISOString() };
    case 'today':
      return { from: now.startOf('day').toISOString(), to: now.endOf('day').toISOString() };
    case 'yesterday':
      return {
        from: now.subtract(1, 'day').startOf('day').toISOString(),
        to: now.subtract(1, 'day').endOf('day').toISOString(),
      };
    case 'this_week':
      return { from: now.startOf('week').toISOString(), to: now.endOf('week').toISOString() };
    case 'this_month':
      return { from: now.startOf('month').toISOString(), to: now.endOf('month').toISOString() };
    default:
      return { from: now.subtract(30, 'day').toISOString(), to: now.toISOString() };
  }
};

const QUICK_RANGES: { labelKey: string; value: QuickRange }[] = [
  { labelKey: 'module.dashBoard.filter.quick.today', value: 'today' },
  { labelKey: 'module.dashBoard.filter.quick.yesterday', value: 'yesterday' },
  { labelKey: 'module.dashBoard.filter.quick.thisWeek', value: 'this_week' },
  { labelKey: 'module.dashBoard.filter.quick.thisMonth', value: 'this_month' },
  { labelKey: 'module.dashBoard.filter.quick.last24h', value: '24h' },
  { labelKey: 'module.dashBoard.filter.quick.last7d', value: '7d' },
  { labelKey: 'module.dashBoard.filter.quick.last30d', value: '30d' },
];

export const DASHBOARD_TABS = [
  'Perpetuals',
  // 'Meme',
  'Prediction',
  'TapGrid',
] as const;
export type DashboardTab = (typeof DASHBOARD_TABS)[number];

const TAB_LABEL_KEYS: Record<DashboardTab, string> = {
  Perpetuals: 'module.dashBoard.filter.tab.perpetuals',
  // Meme: 'module.dashBoard.filter.tab.meme',
  Prediction: 'module.dashBoard.filter.tab.prediction',
  TapGrid: 'module.dashBoard.filter.tab.tapGrid',
};

export const TAB_CONFIG: Record<
  DashboardTab,
  { tradingType: string; depositType: string; withdrawType: string }
> = {
  Perpetuals: { tradingType: 'Perp', depositType: 'DepositFuture', withdrawType: 'WithdrawFuture' },
  // Meme: { tradingType: 'Meme', depositType: 'Deposit', withdrawType: 'Withdraw' },
  Prediction: {
    tradingType: 'Predict',
    depositType: 'DepositPredict',
    withdrawType: 'WithdrawPredict',
  },
  TapGrid: { tradingType: 'TapGrid', depositType: 'RelayDeposit', withdrawType: 'RelayWithdraw' },
};

export const DashboardFilter = () => {
  const t = useTranslate();

  const { filterValues, setFilters } = useListContext();
  const [activeTab, setActiveTab] = useState<DashboardTab>('Perpetuals');
  const [quickRange, setQuickRange] = useState<QuickRange>('30d');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pendingFrom, setPendingFrom] = useState<Dayjs | null>(null);
  const [pendingTo, setPendingTo] = useState<Dayjs | null>(null);

  const handleTabChange = (tab: DashboardTab) => {
    setActiveTab(tab);
    const { tradingType, depositType, withdrawType } = TAB_CONFIG[tab];
    const nextFilters = { ...filterValues, tradingType, depositType, withdrawType };

    if (tab === 'TapGrid') {
      const from = nextFilters.fromTime ? dayjs.utc(nextFilters.fromTime) : null;
      const to = nextFilters.toTime
        ? dayjs.utc(nextFilters.toTime)
        : dayjs.utc().endOf('day');

      if (!from) {
        nextFilters.fromTime = to.subtract(29, 'day').startOf('day').toISOString();
        nextFilters.toTime = to.endOf('day').toISOString();
      } else if (to.diff(from, 'day') > 89) {
        const clampedTo = from.add(89, 'day');
        nextFilters.toTime = (
          clampedTo.isAfter(dayjs.utc()) ? dayjs.utc() : clampedTo
        ).endOf('day').toISOString();
      }
    }

    setFilters(nextFilters);
  };

  const handleQuickRange = (value: QuickRange) => {
    setQuickRange(value);
    const { from, to } = getRange(value);
    setFilters({ fromTime: from, toTime: to });
  };

  const handleOpenPicker = (e: React.MouseEvent<HTMLElement>) => {
    setPendingFrom(filterValues.fromTime ? dayjs.utc(filterValues.fromTime) : null);
    setPendingTo(filterValues.toTime ? dayjs.utc(filterValues.toTime) : null);
    setAnchorEl(e.currentTarget);
  };

  const handleApply = () => {
    if (pendingFrom && pendingTo) {
      setFilters({
        fromTime: pendingFrom.utc().startOf('day').toISOString(),
        toTime: pendingTo.utc().endOf('day').toISOString(),
      });
      setQuickRange(null as any);
    }
    setAnchorEl(null);
  };

  const handleFromChange = (val: Dayjs | null) => {
    setPendingFrom(val);
    if (val && pendingTo) {
      if (pendingTo.isBefore(val, 'day')) {
        setPendingTo(val);
      } else if (isTapGrid && pendingTo.diff(val, 'day') > 89) {
        setPendingTo(val.add(89, 'day'));
      }
    }
  };

  const fromDate = filterValues.fromTime
    ? dayjs.utc(filterValues.fromTime).format('YYYY/MM/DD')
    : '';
  const toDate = filterValues.toTime ? dayjs.utc(filterValues.toTime).format('YYYY/MM/DD') : '';

  const isTapGrid = activeTab === 'TapGrid';

  return (
    <Box sx={{ mb: 2 }}>
      {/* Row 1: Title + Timeframe */}
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" mb={2}>
        <Typography variant="h4" fontWeight="bold">
          {t('resource.dashboard')}
        </Typography>

        <Stack direction="row" alignItems="flex-end" gap={1}>
          <Stack direction="column">
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, ml: 0.5 }}>
              {t('module.dashBoard.filter.timeframe')}
            </Typography>
            <Box
              onClick={handleOpenPicker}
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                px: 2,
                py: 1,
                minWidth: 230,
                bgcolor: 'background.paper',
                cursor: 'pointer',
                '&:hover': { borderColor: 'text.secondary' },
              }}
            >
              <Typography variant="body2">
                {fromDate} - {toDate}
              </Typography>
              <Iconify
                icon="solar:calendar-bold"
                width={20}
                sx={{ ml: 'auto', color: 'text.secondary' }}
              />
            </Box>
          </Stack>

          {QUICK_RANGES.map(({ labelKey, value }) => (
            <Button
              key={value}
              onClick={() => handleQuickRange(value)}
              variant={quickRange === value ? 'contained' : 'outlined'}
              sx={{
                minWidth: 52,
                height: 42,
                borderRadius: 2,
                ...(quickRange === value
                  ? { bgcolor: 'grey.600', '&:hover': { bgcolor: 'grey.700' } }
                  : { borderColor: 'divider', color: 'text.primary' }),
              }}
            >
              {t(labelKey)}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Row 2: Tabs */}
      <Box
        sx={{
          display: 'inline-flex',
          bgcolor: 'grey.100',
          borderRadius: 3,
          p: 0.5,
        }}
      >
        {DASHBOARD_TABS.map((tab) => (
          <Button
            key={tab}
            onClick={() => handleTabChange(tab)}
            // disabled={tab === 'Prediction'}
            disableRipple
            sx={{
              borderRadius: 2.5,
              px: 2.5,
              py: 0.75,
              minWidth: 0,
              border: 'none',
              color: activeTab === tab ? 'text.primary' : 'text.secondary',
              fontWeight: activeTab === tab ? 600 : 400,
              bgcolor: activeTab === tab ? 'background.paper' : 'transparent',
              boxShadow: activeTab === tab ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
              '&:hover': {
                bgcolor: activeTab === tab ? 'background.paper' : 'grey.200',
              },
            }}
          >
            {t(TAB_LABEL_KEYS[tab])}
          </Button>
        ))}
      </Box>

      {/* Date range popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 1 }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" mb={2}>
            {t('module.dashBoard.filter.selectDateRange')}
          </Typography>
          <Stack direction="row" gap={2} alignItems="center">
            <DatePicker
              label={t('module.dashBoard.filter.from')}
              value={pendingFrom}
              onChange={handleFromChange}
              maxDate={dayjs()}
              format="YYYY/MM/DD"
              slotProps={{ textField: { size: 'small' } }}
            />
            <Typography>—</Typography>
            <DatePicker
              label={t('module.dashBoard.filter.to')}
              value={pendingTo}
              onChange={(val) => setPendingTo(val)}
              minDate={pendingFrom ?? undefined}
              maxDate={isTapGrid && pendingFrom ? pendingFrom.add(89, 'day').isAfter(dayjs()) ? dayjs() : pendingFrom.add(89, 'day') : dayjs()}
              format="YYYY/MM/DD"
              slotProps={{ textField: { size: 'small' } }}
            />
          </Stack>
          {isTapGrid && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
              {t('module.dashBoard.filter.tapGridMaxRange', { _: 'The query date range cannot exceed 90 days' })}
            </Typography>
          )}
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="flex-end" gap={1}>
            <Button size="small" onClick={() => setAnchorEl(null)}>
              {t('ra.action.cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!pendingFrom || !pendingTo}
              onClick={handleApply}
            >
              {t('module.dashBoard.filter.apply')}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};
